import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useAuthUser } from 'auth/hooks/auth';
import { Error, PageLayout } from 'core/components';
import { StatusCode } from 'core/constants';
import { Loader } from 'core/elements';
import { SelectionCountProvider } from 'core/states';
import { CustomerQuotePartList, VendorQuotePartList } from 'quotes/components';
import { QuoteEntityType, QuoteType } from 'quotes/enums';
import { QuoteHelper } from 'quotes/helpers';
import { useGetQuote } from 'quotes/hooks';
import { CustomerQuotePartsProvider } from 'quotes/states';
import { SelectedQuotePartProvider } from 'quotes/states/selectedQuotePart';
import { IVendorQuote } from 'quotes/types';
import { getQuoteVersion } from 'quotes/utils';

export const OrderPartsPage = () => {
  const { quoteId } = useParams();

  const { t } = useTranslation();

  const { user } = useAuthUser();
  const { record, extraData, isLoading, isError } = useGetQuote({ quoteId, isOrder: 1 });

  const quoteType = useMemo(
    () => QuoteHelper.determineType(record, user?.companyId),
    [record, user]
  );

  const initialQuoteVersion = getQuoteVersion(record);

  return (
    <PageLayout
      title={t('titleCustomerOrderParts', {
        quoteId: quoteId,
      })}
    >
      {isLoading && <Loader />}

      {isError && <Error statusCode={StatusCode.NOT_FOUND} />}

      <SelectionCountProvider>
        <SelectedQuotePartProvider>
          {QuoteType.CUSTOMER === quoteType && (
            <CustomerQuotePartsProvider initialQuoteVersion={initialQuoteVersion}>
              <CustomerQuotePartList
                initialQuote={record}
                extraQuoteData={extraData}
                quoteEntityType={QuoteEntityType.ORDER}
              />
            </CustomerQuotePartsProvider>
          )}

          {QuoteType.VENDOR === quoteType && (
            <VendorQuotePartList
              quote={record as IVendorQuote}
              quoteEntityType={QuoteEntityType.ORDER}
            />
          )}
        </SelectedQuotePartProvider>
      </SelectionCountProvider>
    </PageLayout>
  );
};
